import { Controller } from "@hotwired/stimulus"
import mux from "mux-embed"

// Initializes and cleans up MUX Data quality-of-experience monitoring.
// Connects to data-controller="mux-data-tracker"
export default class extends Controller {
  static values = {
    itemId: String,
    itemTitle: String,
    spaceId: String,
    envKey: { type: String, default: "" },
    viewerUserId: { type: String, default: "" },
  }

  declare itemIdValue: string
  declare itemTitleValue: string
  declare spaceIdValue: string
  declare envKeyValue: string
  declare viewerUserIdValue: string

  private initialized = false
  private monitoredElement: HTMLElement | null = null
  private monitoredElementId: string | null = null
  // The track the live monitor was armed for, so a re-arm can tell a genuine
  // track change from a value callback that only echoes wiring already done.
  private monitoredItemId: string | null = null

  connect() {
    this.initialized = false
    this.initializeMonitoring()
  }

  disconnect() {
    this.cleanupMonitoring()
    this.initialized = false
  }

  // The unfurl player (#1643) swaps the transport element in place; the
  // monitored <mux-audio> is then detached. initializeMonitoring already
  // tears down monitoring of a superseded element before re-arming.
  // Wired from shared/_audio_player as embed-player:mediaswapped.
  refresh() {
    this.initializeMonitoring()
  }

  // When both tracks are Mux-backed the unfurl player re-sources the same
  // element rather than swapping it — no `mediaswapped`, so this value change
  // is the only signal that a different track is playing. Re-arm, or the whole
  // session's QoE stays filed under the track that happened to be first.
  // Inert everywhere else: nothing is monitored before connect() (Stimulus
  // fires this once at startup) and no other page rewrites the value. A swap
  // announcement has already re-armed with this id by the time the callback
  // lands, so the id guard keeps that from filing a second view.
  itemIdValueChanged(value: string) {
    if (!this.initialized || value === this.monitoredItemId) return

    this.cleanupMonitoring()
    this.initializeMonitoring()
  }

  private initializeMonitoring() {
    if (!this.envKeyValue) return

    const muxAudio = this.element.querySelector("mux-audio")
    if (!muxAudio || !(muxAudio instanceof HTMLElement)) {
      // A swap onto a direct-content track leaves the monitored <mux-audio>
      // detached; monitoring it further reports against a node that is no
      // longer in the document. No-op when nothing was monitored to begin
      // with, which is every non-Mux page.
      this.cleanupMonitoring()
      return
    }

    if (
      this.initialized &&
      this.monitoredElement &&
      this.monitoredElement !== muxAudio
    ) {
      this.cleanupMonitoring()
    }

    if (this.initialized) return

    try {
      const playerId = muxAudio.id || `mux-audio-${this.itemIdValue}`
      if (!muxAudio.id) {
        muxAudio.id = playerId
      }

      mux.monitor(`#${playerId}`, {
        debug: false,
        data: {
          env_key: this.envKeyValue,
          viewer_user_id: this.viewerUserIdValue?.trim() || undefined,
          video_id: this.itemIdValue || undefined,
          video_title: this.itemTitleValue || undefined,
          sub_property_id: this.spaceIdValue || undefined,
          custom_1: "web",
          custom_2: "stream",
        },
      })
      this.initialized = true
      this.monitoredElement = muxAudio
      this.monitoredElementId = playerId
      this.monitoredItemId = this.itemIdValue
    } catch (error) {
      console.error("Failed to initialize MUX Data tracking:", error)
    }
  }

  private cleanupMonitoring() {
    if (!this.initialized || !this.monitoredElementId) return

    try {
      mux.emit(`#${this.monitoredElementId}`, "destroy")
    } catch (error) {
      console.warn("Failed to cleanup MUX monitoring:", error)
    } finally {
      this.initialized = false
      this.monitoredElement = null
      this.monitoredElementId = null
      this.monitoredItemId = null
    }
  }
}
